import { useEffect, useState } from 'react';
import { Building2, Plus, Edit, Trash2, PlayCircle, X, Save, CheckCircle, Users } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'react-hot-toast';
import ConfirmDialog from '../../components/common/ConfirmDialog';

const OrganizationManagement = () => {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showScenariosModal, setShowScenariosModal] = useState(false);
  const [showAccountsModal, setShowAccountsModal] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    parentId: '',
    managerLimit: '',
    supervisorLimit: '',
  });

  // Scenarios state
  const [allScenarios, setAllScenarios] = useState([]);
  const [organizationScenarios, setOrganizationScenarios] = useState([]);
  const [loadingScenarios, setLoadingScenarios] = useState(false);
  const [organizationUsers, setOrganizationUsers] = useState([]);
  const [organizationInvitations, setOrganizationInvitations] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    confirmText: 'Подтвердить',
    isLoading: false,
  });

  useEffect(() => {
    loadOrganizations();
    loadAllScenarios();
  }, []);

  const loadOrganizations = async () => {
    try {
      const response = await api.get('/api/admin/organizations');
      setOrganizations(response.data.organizations || []);
    } catch (error) {
      console.error('Error loading organizations:', error);
      toast.error('Ошибка загрузки организаций');
    } finally {
      setLoading(false);
    }
  };

  const loadAllScenarios = async () => {
    try {
      const response = await api.get('/api/admin/scenarios');
      setAllScenarios(response.data.scenarios || []);
    } catch (error) {
      console.error('Error loading scenarios:', error);
    }
  };

  const openCreateModal = () => {
    setFormData({ name: '', description: '', parentId: '', managerLimit: '', supervisorLimit: '' });
    setShowCreateModal(true);
  };

  const openEditModal = (org) => {
    setSelectedOrganization(org);
    setFormData({
      name: org.name,
      description: org.description || '',
      parentId: org.parent_id || '',
      managerLimit: org.manager_limit ?? '',
      supervisorLimit: org.supervisor_limit ?? '',
    });
    setShowEditModal(true);
  };

  const openScenariosModal = async (org) => {
    setSelectedOrganization(org);
    setShowScenariosModal(true);
    setLoadingScenarios(true);

    try {
      const response = await api.get(`/api/admin/organizations/${org.id}/scenarios`);
      setOrganizationScenarios(response.data.scenarios || []);
    } catch (error) {
      console.error('Error loading organization scenarios:', error);
      toast.error('Ошибка загрузки сценариев организации');
    } finally {
      setLoadingScenarios(false);
    }
  };

  const openAccountsModal = async (org) => {
    setSelectedOrganization(org);
    setShowAccountsModal(true);
    setLoadingAccounts(true);
    try {
      const [usersResponse, invitationsResponse] = await Promise.all([
        api.get(`/api/admin/organizations/${org.id}/users`),
        api.get(`/api/admin/organizations/${org.id}/invitations`),
      ]);
      setOrganizationUsers(usersResponse.data.users || []);
      setOrganizationInvitations(invitationsResponse.data.invitations || []);
    } catch (error) {
      console.error('Error loading organization accounts:', error);
      toast.error('Ошибка загрузки аккаунтов организации');
    } finally {
      setLoadingAccounts(false);
    }
  };

  const createOrganization = async () => {
    if (!formData.name.trim()) {
      toast.error('Введите название организации');
      return;
    }

    try {
      await api.post('/api/admin/organizations', formData);
      toast.success('Организация создана');
      setShowCreateModal(false);
      loadOrganizations();
    } catch (error) {
      console.error('Error creating organization:', error);
      toast.error(error.response?.data?.error || 'Ошибка создания организации');
    }
  };

  const updateOrganization = async () => {
    if (!formData.name.trim()) {
      toast.error('Введите название организации');
      return;
    }

    try {
      await api.put(`/api/admin/organizations/${selectedOrganization.id}`, formData);
      toast.success('Организация обновлена');
      setShowEditModal(false);
      loadOrganizations();
    } catch (error) {
      console.error('Error updating organization:', error);
      toast.error(error.response?.data?.error || 'Ошибка обновления организации');
    }
  };

  const deleteOrganization = (org) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Удалить организацию',
      message: `Вы уверены, что хотите удалить организацию "${org.name}"? Все пользователи потеряют связь с организацией. Это действие нельзя отменить.`,
      confirmText: 'Удалить',
      isLoading: false,
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, isLoading: true }));
        try {
          await api.delete(`/api/admin/organizations/${org.id}`);
          toast.success('Организация удалена');
          loadOrganizations();
          setConfirmDialog(prev => ({ ...prev, isOpen: false, isLoading: false }));
        } catch (error) {
          console.error('Error deleting organization:', error);
          toast.error(error.response?.data?.error || 'Ошибка удаления организации');
          setConfirmDialog(prev => ({ ...prev, isLoading: false }));
        }
      },
    });
  };

  const toggleOrganizationActive = (org) => {
    setConfirmDialog({
      isOpen: true,
      title: org.is_active ? 'Деактивировать организацию' : 'Активировать организацию',
      message: `Вы уверены, что хотите ${org.is_active ? 'деактивировать' : 'активировать'} организацию "${org.name}"?`,
      confirmText: org.is_active ? 'Деактивировать' : 'Активировать',
      isLoading: false,
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, isLoading: true }));
        try {
          await api.patch(`/api/admin/organizations/${org.id}/toggle-active`);
          toast.success(org.is_active ? 'Организация деактивирована' : 'Организация активирована');
          loadOrganizations();
          setConfirmDialog(prev => ({ ...prev, isOpen: false, isLoading: false }));
        } catch (error) {
          console.error('Error toggling organization:', error);
          toast.error('Ошибка изменения статуса');
          setConfirmDialog(prev => ({ ...prev, isLoading: false }));
        }
      },
    });
  };

  const assignScenarios = async (scenarioIds) => {
    try {
      await api.post(`/api/admin/organizations/${selectedOrganization.id}/scenarios`, {
        scenarioIds
      });
      toast.success('Сценарии назначены организации');

      // Reload scenarios
      const response = await api.get(`/api/admin/organizations/${selectedOrganization.id}/scenarios`);
      setOrganizationScenarios(response.data.scenarios || []);
    } catch (error) {
      console.error('Error assigning scenarios:', error);
      toast.error(error.response?.data?.error || 'Ошибка назначения сценариев');
    }
  };

  const removeScenario = async (scenarioId) => {
    try {
      await api.delete(`/api/admin/organizations/${selectedOrganization.id}/scenarios/${scenarioId}`);
      toast.success('Сценарий удалён из организации');

      // Reload scenarios
      const response = await api.get(`/api/admin/organizations/${selectedOrganization.id}/scenarios`);
      setOrganizationScenarios(response.data.scenarios || []);
    } catch (error) {
      console.error('Error removing scenario:', error);
      toast.error('Ошибка удаления сценария');
    }
  };

  const isScenarioAssigned = (scenarioId) => {
    return organizationScenarios.some(s => s.scenario_id === scenarioId);
  };

  const handleBulkAssign = () => {
    const unassignedScenarios = allScenarios
      .filter(s => s.is_active && !isScenarioAssigned(s.id))
      .map(s => s.id);

    if (unassignedScenarios.length === 0) {
      toast.error('Все активные сценарии уже назначены');
      return;
    }

    assignScenarios(unassignedScenarios);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Загрузка организаций...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Управление организациями</h1>
          <p className="text-gray-600 mt-1">Франшизы, лимиты сотрудников и доступные сценарии</p>
        </div>
        <button
          onClick={openCreateModal}
          className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
        >
          <Plus className="w-5 h-5" />
          Создать организацию
        </button>
      </div>

      {/* Organizations List */}
      {organizations.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Нет организаций</h3>
          <p className="text-gray-600 mb-6">Создайте первую организацию для начала работы</p>
          <button
            onClick={openCreateModal}
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            <Plus className="w-5 h-5" />
            Создать организацию
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {organizations.map((org) => (
            <div
              key={org.id}
              className={`bg-white rounded-xl border ${
                org.is_active ? 'border-gray-200' : 'border-gray-300 opacity-60'
              } p-6 hover:shadow-lg transition-shadow`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Building2 className={`w-5 h-5 ${org.is_active ? 'text-blue-600' : 'text-gray-400'}`} />
                    <h3 className="font-semibold text-gray-900">{org.name}</h3>
                  </div>
                  {org.description && (
                    <p className="text-sm text-gray-600">{org.description}</p>
                  )}
                </div>
                <button
                  onClick={() => toggleOrganizationActive(org)}
                  className={`px-3 py-1 rounded-full text-xs font-medium ${
                    org.is_active
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {org.is_active ? 'Активна' : 'Неактивна'}
                </button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-xs text-gray-500">Менеджеры</p>
                  <p className="text-lg font-semibold text-gray-900">{org.manager_count || 0} / {org.manager_limit ?? '∞'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Супервайзеры</p>
                  <p className="text-lg font-semibold text-gray-900">{org.supervisor_count || 0} / {org.supervisor_limit ?? '∞'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Ожидают входа</p>
                  <p className="text-lg font-semibold text-gray-900">{org.pending_invitation_count || 0}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Сценарии</p>
                  <p className="text-lg font-semibold text-gray-900">{org.scenario_count || 0}</p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => openAccountsModal(org)}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition-colors text-sm font-medium"
                >
                  <Users className="w-4 h-4" />
                  Аккаунты
                </button>
                <button
                  onClick={() => openScenariosModal(org)}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium"
                >
                  <PlayCircle className="w-4 h-4" />
                  Сценарии
                </button>
                <button
                  onClick={() => openEditModal(org)}
                  className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Редактировать"
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  onClick={() => deleteOrganization(org)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Удалить"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500">
                Создано: {new Date(org.created_at).toLocaleDateString()}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">Создать организацию</h2>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Название организации *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Например: Филиал Москва"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Головная организация</label>
                <select value={formData.parentId} onChange={(e) => setFormData({ ...formData, parentId: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">Нет — головная организация</option>
                  {organizations.map((org) => <option key={org.id} value={org.id}>{org.name}</option>)}
                </select>
                <p className="mt-1 text-xs text-gray-500">Директор головной организации увидит эту франшизу в своём обзоре.</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-sm font-medium text-gray-700 mb-2">Лимит менеджеров</label><input type="number" min="0" value={formData.managerLimit} onChange={(e) => setFormData({ ...formData, managerLimit: e.target.value })} placeholder="Без лимита" className="w-full px-4 py-2 border border-gray-300 rounded-lg" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-2">Лимит супервайзеров</label><input type="number" min="0" value={formData.supervisorLimit} onChange={(e) => setFormData({ ...formData, supervisorLimit: e.target.value })} placeholder="Без лимита" className="w-full px-4 py-2 border border-gray-300 rounded-lg" /></div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Описание
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Краткое описание организации"
                />
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => setShowCreateModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Отмена
              </button>
              <button
                onClick={createOrganization}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Создать
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedOrganization && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">Редактировать организацию</h2>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Название организации *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Головная организация</label>
                <select value={formData.parentId} onChange={(e) => setFormData({ ...formData, parentId: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg">
                  <option value="">Нет — головная организация</option>
                  {organizations.filter((org) => org.id !== selectedOrganization.id).map((org) => <option key={org.id} value={org.id}>{org.name}</option>)}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-sm font-medium text-gray-700 mb-2">Лимит менеджеров</label><input type="number" min="0" value={formData.managerLimit} onChange={(e) => setFormData({ ...formData, managerLimit: e.target.value })} placeholder="Без лимита" className="w-full px-4 py-2 border border-gray-300 rounded-lg" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-2">Лимит супервайзеров</label><input type="number" min="0" value={formData.supervisorLimit} onChange={(e) => setFormData({ ...formData, supervisorLimit: e.target.value })} placeholder="Без лимита" className="w-full px-4 py-2 border border-gray-300 rounded-lg" /></div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Описание
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => setShowEditModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Отмена
              </button>
              <button
                onClick={updateOrganization}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                <Save className="w-4 h-4" />
                Сохранить
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Scenarios Modal */}
      {showScenariosModal && selectedOrganization && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Сценарии организации</h2>
                  <p className="text-gray-600 mt-1">{selectedOrganization.name}</p>
                </div>
                <button
                  onClick={() => setShowScenariosModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6 text-gray-500" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {loadingScenarios ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600">Загрузка сценариев...</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Assigned Scenarios */}
                  {organizationScenarios.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                          <CheckCircle className="w-5 h-5 text-green-600" />
                          Назначенные сценарии ({organizationScenarios.length})
                        </h3>
                      </div>
                      <div className="grid gap-4">
                        {organizationScenarios.map((scenario) => (
                          <div
                            key={scenario.scenario_id}
                            className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start justify-between"
                          >
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-900">{scenario.name}</h4>
                              <p className="text-sm text-gray-600 mt-1">{scenario.description}</p>
                              <div className="flex items-center gap-2 mt-2">
                                <span className={`text-xs px-2 py-1 rounded ${
                                  scenario.is_active
                                    ? 'bg-white border border-green-300 text-green-700'
                                    : 'bg-gray-200 text-gray-600'
                                }`}>
                                  {scenario.is_active ? 'Активен' : 'Неактивен'}
                                </span>
                              </div>
                            </div>
                            <button
                              onClick={() => removeScenario(scenario.scenario_id)}
                              className="ml-4 p-2 hover:bg-red-100 rounded-lg transition-colors group"
                              title="Удалить"
                            >
                              <X className="w-5 h-5 text-red-600 group-hover:text-red-700" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Available Scenarios */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <Plus className="w-5 h-5 text-blue-600" />
                        Доступные сценарии
                      </h3>
                      {allScenarios.filter(s => s.is_active && !isScenarioAssigned(s.id)).length > 0 && (
                        <button
                          onClick={handleBulkAssign}
                          className="text-sm px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          Назначить все
                        </button>
                      )}
                    </div>
                    {allScenarios.filter(s => s.is_active && !isScenarioAssigned(s.id)).length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <p>Все активные сценарии уже назначены этой организации</p>
                      </div>
                    ) : (
                      <div className="grid gap-4">
                        {allScenarios
                          .filter(s => s.is_active && !isScenarioAssigned(s.id))
                          .map((scenario) => (
                            <div
                              key={scenario.id}
                              className="bg-white border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors"
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <h4 className="font-semibold text-gray-900">{scenario.name}</h4>
                                  <p className="text-sm text-gray-600 mt-1">{scenario.description}</p>
                                </div>
                                <button
                                  onClick={() => assignScenarios([scenario.id])}
                                  className="ml-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                                >
                                  Назначить
                                </button>
                              </div>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowScenariosModal(false)}
                className="w-full px-4 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors font-medium"
              >
                Готово
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Accounts transparency modal */}
      {showAccountsModal && selectedOrganization && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div><h2 className="text-2xl font-bold text-gray-900">Аккаунты организации</h2><p className="text-gray-600 mt-1">{selectedOrganization.name}</p></div>
              <button onClick={() => setShowAccountsModal(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-6 h-6 text-gray-500" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-7">
              {loadingAccounts ? <div className="py-12 text-center text-gray-500">Загрузка...</div> : <>
                <section>
                  <h3 className="font-semibold text-gray-900 mb-3">Созданные аккаунты ({organizationUsers.length})</h3>
                  <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                    {organizationUsers.length === 0 ? <p className="p-4 text-gray-500">Аккаунтов пока нет.</p> : organizationUsers.map((user) => (
                      <div key={user.id} className="p-4 flex items-center justify-between gap-3">
                        <div><p className="font-medium text-gray-900">{user.first_name} {user.last_name}</p><p className="text-sm text-gray-500">{user.email} · {user.role === 'director' ? 'Директор' : user.role === 'supervisor' ? 'Супервайзер' : 'Менеджер'}</p></div>
                        <span className={`text-xs px-2 py-1 rounded-full ${user.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{user.is_active ? 'Активен' : 'Заблокирован'}</span>
                      </div>
                    ))}
                  </div>
                </section>
                <section>
                  <h3 className="font-semibold text-gray-900 mb-3">История приглашений ({organizationInvitations.length})</h3>
                  <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                    {organizationInvitations.length === 0 ? <p className="p-4 text-gray-500">Приглашений пока нет.</p> : organizationInvitations.map((invitation) => (
                      <div key={invitation.id} className="p-4 flex items-center justify-between gap-3">
                        <div><p className="font-medium text-gray-900">{invitation.first_name} {invitation.last_name}</p><p className="text-sm text-gray-500">{invitation.email} · {invitation.role === 'supervisor' ? 'Супервайзер' : 'Менеджер'}</p></div>
                        <span className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-800">{invitation.status === 'pending' ? 'Ожидает' : invitation.status === 'accepted' ? 'Принято' : invitation.status === 'expired' ? 'Истекло' : 'Отозвано'}</span>
                      </div>
                    ))}
                  </div>
                </section>
              </>}
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        isLoading={confirmDialog.isLoading}
      />
    </div>
  );
};

export default OrganizationManagement;
